import React from 'react'
import { usePage, Link } from '@inertiajs/react'
import {
  LayoutDashboard,
  CalendarDays,
  BarChart3,
  Layers,
  FileEdit,
  Flag,
  History,
  HelpCircle,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  TrendingUp,
  ClipboardList,
  Telescope,
  Target,
  Crown,
  Megaphone,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBrand } from '@/hooks/useBrand'
import type { ReportingScope } from '@/types/shared'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface SidebarProps {
  /**
   * This property's canonical path, served by the backend (`property.basePath`). Every nav item is
   * this plus a FIXED suffix — the sidebar never assembles the identity part of a URL.
   */
  basePath: string
  /** The owning business's portfolio path (`currentAccount.basePath`). Null only if the prop lags. */
  accountBasePath: string | null
  open: boolean
  onClose: () => void
  collapsed: boolean
  onToggleCollapse: () => void
}

interface NavItem {
  label: string
  icon: React.ElementType
  href: string
  activePrefix?: string
  /** A quiet teal dot — "there is something here you have not read". Only "What's new" uses it. */
  dot?: boolean
}

// The group this property belongs to, when it belongs to one. `null` for an ungrouped property,
// which is every property in a business that does not use groups — so the strip below simply is not
// rendered and the sidebar is byte-identical to life before groups existed.
//
// The subset of ReportingScope (types/shared.ts) this strip reads — DERIVED from it rather than
// re-declared, so `tsc --noEmit` enforces the "kept in step" note that used to sit here instead of
// a reader remembering to. `usePage<>` is an unchecked cast, so a hand-written twin could drift
// silently; the one that matters is `leadBasePath` — the lead's backend-served canonical path, the
// ONE link in here that names a hotel other than the page's own, so it can never be derived from
// `basePath` and a rename would render an `undefined` href with nothing failing.
type SidebarReportingScope = Pick<
  ReportingScope,
  'kind' | 'leadPropertyName' | 'leadBasePath' | 'isLead' | 'propertyCount'
>


export default function Sidebar({ basePath, accountBasePath, open, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  const brand = useBrand()
  const page = usePage<{ reportingScope?: SidebarReportingScope | null; whatsNewUnseen?: boolean }>()
  const { url } = page
  const scope = page.props.reportingScope ?? null
  // The feed dot. Shared prop on every operator page (the feed is global, like the guides), and the
  // SERVER decides — seen-state is a per-user watermark, never localStorage, because an operator
  // reading the news on the front desk machine has read it on their phone too.
  const whatsNewUnseen = page.props.whatsNewUnseen ?? false
  const base = basePath

  const navGroups: { label: string; items: NavItem[] }[] = [
    {
      label: 'Overview',
      items: [
        { label: 'Dashboard', icon: LayoutDashboard, href: base },
      ],
    },
    {
      label: 'Calendar',
      items: [
        { label: 'Prices', icon: CalendarDays, href: `${base}/calendar/prices` },
        { label: 'Occupancy', icon: BarChart3, href: `${base}/calendar/occupancy` },
      ],
    },
    {
      label: 'Pricing',
      items: [
        { label: 'Pricing Rules', icon: Layers, href: `${base}/pricing-rules/rooms-setup`, activePrefix: `${base}/pricing-rules` },
        { label: 'Targets', icon: Target, href: `${base}/targets` },
      ],
    },
    {
      label: 'Reports',
      items: [
        { label: 'Snapshot', icon: Telescope, href: `${base}/reports/snapshot` },
        { label: 'Reservations', icon: ClipboardList, href: `${base}/reports/reservations` },
      ],
    },
    {
      label: 'Management',
      items: [
        { label: 'Overrides', icon: FileEdit, href: `${base}/overrides` },
        { label: 'Event Catalogue', icon: Flag, href: `${base}/events` },
        // One item, two tabs (Prices, Changes): lit on either (docs/plans/2026-10-20-change-safety.md R2.6).
        { label: 'History', icon: History, href: `${base}/history/prices`, activePrefix: `${base}/history` },
        { label: 'Market Intelligence', icon: TrendingUp, href: `${base}/market-intelligence`, activePrefix: `${base}/market-intelligence` },
      ],
    },
    {
      label: 'Setup',
      items: [
        { label: 'Add Property', icon: Plus, href: '/onboarding' },
      ],
    },
    {
      label: 'Support',
      items: [
        { label: "What's new", icon: Megaphone, href: '/whats-new', dot: whatsNewUnseen },
        { label: 'Help & Guides', icon: HelpCircle, href: '/guides' },
      ],
    },
  ]

  function isActive(item: NavItem) {
    const href = item.activePrefix || item.href
    if (href === base) return url === base || url === `${base}/`
    return url.startsWith(href)
  }

  function NavButton({ item, isCollapsed }: { item: NavItem; isCollapsed: boolean }) {
    const Icon = item.icon
    const active = isActive(item)

    const link = (
      <Link
        href={item.href}
        onClick={onClose}
        className={cn(
          'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150',
          isCollapsed && 'justify-center px-0',
          active
            ? 'bg-sidebar-active text-white shadow-sm shadow-sidebar-active/25'
            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-white'
        )}
      >
        <span className="relative flex shrink-0 items-center">
          <Icon className={cn(
            'h-4 w-4 shrink-0 transition-colors',
            active ? 'text-white' : 'text-sidebar-muted group-hover:text-sidebar-foreground'
          )} />
          {/* Collapsed, the label is gone, so the dot rides the icon instead of the row. */}
          {item.dot && isCollapsed && (
            <span
              data-testid="nav-dot"
              aria-hidden="true"
              className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-sidebar-active ring-2 ring-sidebar"
            />
          )}
        </span>
        {!isCollapsed && <span className="sidebar-label">{item.label}</span>}
        {item.dot && !isCollapsed && (
          <span data-testid="nav-dot" aria-hidden="true" className="ml-auto h-2 w-2 shrink-0 rounded-full bg-sidebar-active" />
        )}
      </Link>
    )

    if (isCollapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={12}
            className="rounded-lg border-0 bg-sidebar px-3 py-1.5 text-xs font-medium text-white shadow-xl shadow-black/20"
          >
            {item.label}
          </TooltipContent>
        </Tooltip>
      )
    }

    return link
  }

  // TooltipProvider wraps the WHOLE panel, not just the nav: the collapsed rail shows tooltips for
  // the nav items AND for the group-context badge above them.
  const sidebarContent = (isCollapsed: boolean) => {
    const logoClass = cn(
      'flex h-16 items-center gap-2.5 px-5 shrink-0 transition-opacity hover:opacity-90',
      isCollapsed && 'justify-center px-0'
    )
    const logoInner = (
      <>
        <div className="flex h-8 w-8 shrink-0 flex-col items-center justify-center gap-[3px] rounded-lg bg-sidebar-active" aria-hidden="true">
          <span className="h-[3px] w-4 rounded-full bg-white" />
          <span className="h-[3px] w-3 rounded-full bg-white/70" />
          <span className="h-[3px] w-2 rounded-full bg-white/40" />
        </div>
        {!isCollapsed && (
          <div className="sidebar-label">
            {/* Lowercase wordmark sized up so its x-height carries the presence caps would. */}
            <span className="text-lg font-semibold lowercase leading-none text-white tracking-tight">{brand}</span>
            <span className="block text-[10px] font-medium uppercase tracking-widest text-sidebar-muted">Revenue Management</span>
          </div>
        )}
      </>
    )

    return (
    <TooltipProvider delayDuration={0}>
    <div className="flex h-full flex-col">
      {/* Logo → this business's portfolio briefing. The path is backend-served; with no account
          prop the wordmark still renders, just not as a link (never a guessed URL). */}
      {accountBasePath ? (
        <Link href={accountBasePath} onClick={onClose} className={logoClass}>
          {logoInner}
        </Link>
      ) : (
        <div className={logoClass}>{logoInner}</div>
      )}

      {/* COMBINED INVENTORY CONTEXT: rendered only for a listing in a combined inventory.
          A following listing's pricing pages redirect to its lead listing, so the sidebar has to say
          why: it names the feature, says whether this listing leads it, and links a following listing
          to the lead. The combined inventory has no name of its own (it is called by its lead
          listing's), so the lead is named once, on the following listing's link, and never beside
          itself. Other properties render nothing here at all. */}
      {scope && !isCollapsed && (
        <div className="mx-3 mb-3 shrink-0 rounded-lg border border-sidebar-accent bg-sidebar-accent/40 px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted">
            {scope.isLead ? <Crown className="h-3 w-3 text-sidebar-active" /> : <Layers className="h-3 w-3" />}
            Combined inventory
          </p>
          <p className="mt-1 truncate text-[13px] font-medium text-white">
            {scope.isLead ? 'Lead listing' : 'Following listing'}
          </p>
          {scope.isLead ? (
            <p className="mt-0.5 text-[11px] text-sidebar-muted">
              Prices here cover {scope.propertyCount} {scope.propertyCount === 1 ? 'listing' : 'listings'}
            </p>
          ) : (
            scope.leadBasePath ? (
              <Link
                href={scope.leadBasePath}
                onClick={onClose}
                className="mt-0.5 block truncate text-[11px] text-sidebar-muted transition-colors hover:text-white"
              >
                Priced at {scope.leadPropertyName}
              </Link>
            ) : (
              // A lead the person cannot open (change safety R4.3): no name and no link to it.
              <p className="mt-0.5 truncate text-[11px] text-sidebar-muted">Priced at the lead listing</p>
            )
          )}
        </div>
      )}
      {scope && isCollapsed && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mx-auto mb-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sidebar-accent/60">
              {scope.isLead ? (
                <Crown className="h-3.5 w-3.5 text-sidebar-active" />
              ) : (
                <Layers className="h-3.5 w-3.5 text-sidebar-muted" />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={12}
            className="rounded-lg border-0 bg-sidebar px-3 py-1.5 text-xs font-medium text-white shadow-xl shadow-black/20"
          >
            {scope.isLead
              ? `Lead listing · prices here cover ${scope.propertyCount} ${scope.propertyCount === 1 ? 'listing' : 'listings'}`
              : `Following listing · priced at ${scope.leadPropertyName ?? 'the lead listing'}`}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Navigation */}
      <nav className={cn(
        'flex-1 overflow-y-auto pb-4 pt-2',
        isCollapsed ? 'px-2' : 'px-3'
      )}>
        {navGroups.map((group) => (
          <div key={group.label} className="mb-4">
            {!isCollapsed && (
              <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted">
                {group.label}
              </p>
            )}
            {isCollapsed && (
              <div className="mx-auto mb-1.5 h-px w-6 bg-sidebar-accent" />
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavButton key={item.href} item={item} isCollapsed={isCollapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Collapse toggle (desktop only) */}
      <div className={cn(
        'hidden shrink-0 border-t border-sidebar-accent p-3 lg:block',
        isCollapsed && 'px-2'
      )}>
        <button
          onClick={onToggleCollapse}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium text-sidebar-muted cursor-pointer transition-all duration-150 hover:bg-sidebar-accent hover:text-white',
            isCollapsed && 'justify-center px-0'
          )}
        >
          {isCollapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <>
              <PanelLeftClose className="h-4 w-4" />
              <span className="sidebar-label">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
    </TooltipProvider>
    )
  }

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Mobile sidebar (always expanded) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-sidebar transition-transform duration-300 ease-in-out lg:hidden',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="absolute right-3 top-4">
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-sidebar-muted cursor-pointer hover:bg-sidebar-accent hover:text-white transition-colors"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {sidebarContent(false)}
      </aside>

      {/* Desktop sidebar (collapsible) */}
      <aside
        className={cn(
          'hidden shrink-0 bg-sidebar lg:block sidebar-transition',
          collapsed ? 'w-16' : 'w-64',
          collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'
        )}
      >
        {sidebarContent(collapsed)}
      </aside>
    </>
  )
}
